const EPS = 1e-6
const ROUND_DIGITS = 6

const clamp = (value, min, max) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return min
  if (n <= min) return min
  if (n >= max) return max
  return n
}

const round = (value, digits = ROUND_DIGITS) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  const p = 10 ** digits
  return Math.round(n * p) / p
}

const normalizeLength = (clipLength) => {
  const len = Number(clipLength)
  return Number.isFinite(len) && len > 0 ? len : 0
}

const normalizeSpeed = (speed, fallback = 1) => {
  const n = Number(speed)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return n
}

const normalizeAffects = (affects) => (affects === 'right' ? 'right' : 'left')

export function pointToLocalTime(point, clipLength) {
  const len = normalizeLength(clipLength)
  const localFromT = Number(point?.t)
  if (Number.isFinite(localFromT)) return clamp(localFromT, 0, len)
  const u = Number(point?.u)
  if (Number.isFinite(u)) return clamp(u, 0, 1) * len
  return 0
}

export function pointToNormalizedPosition(point, clipLength) {
  const u = Number(point?.u)
  if (Number.isFinite(u)) return clamp(u, 0, 1)
  const len = normalizeLength(clipLength)
  if (len <= EPS) return 0
  const t = Number(point?.t)
  if (!Number.isFinite(t)) return 0
  return clamp(t / len, 0, 1)
}

export function normalizeRetimePoints(points, clipLength) {
  const list = Array.isArray(points) ? points : []
  return list
    .map((p) => ({
      u: round(pointToNormalizedPosition(p, clipLength)),
      speed: round(normalizeSpeed(p?.speed, 1)),
      affects: normalizeAffects(p?.affects),
    }))
    .sort((a, b) => a.u - b.u)
}

export function localizeRetimePoints(points, clipLength) {
  const len = normalizeLength(clipLength)
  const normalized = normalizeRetimePoints(points, len)
  return normalized.map((p) => ({
    t: round(p.u * len),
    speed: p.speed,
    affects: p.affects,
  }))
}

function getRawStripRetimePoints(strip) {
  if (!strip?.retime) return []
  if (Array.isArray(strip.retime.points) && strip.retime.points.length) return strip.retime.points
  if (Array.isArray(strip.retime.pointsNormalized) && strip.retime.pointsNormalized.length) {
    return strip.retime.pointsNormalized
  }
  return []
}

export function getStripRetimePointsNormalized(strip, clipLength = null) {
  const len = clipLength == null ? normalizeLength(strip?.length) : normalizeLength(clipLength)
  return normalizeRetimePoints(getRawStripRetimePoints(strip), len)
}

export function getStripRetimePointsLocal(strip, clipLength = null) {
  const len = clipLength == null ? normalizeLength(strip?.length) : normalizeLength(clipLength)
  return localizeRetimePoints(getRawStripRetimePoints(strip), len)
}

export function hasStripRetimePoints(strip) {
  return getRawStripRetimePoints(strip).length > 0
}

export function isStripRetimeEnabled(strip) {
  return !!(strip?.retime && strip.retime.enabled)
}

export function setStripRetimePointsNormalized(strip, points, clipLength = null) {
  if (!strip) return []
  if (!strip.retime || typeof strip.retime !== 'object') {
    strip.retime = { enabled: false, points: [], mode: 'step' }
  }
  const len = clipLength == null ? normalizeLength(strip.length) : normalizeLength(clipLength)
  const normalized = normalizeRetimePoints(points, len)
  strip.retime.points = normalized
  // Legacy fallback field; keep project shape stable and canonical going forward.
  if (Object.prototype.hasOwnProperty.call(strip.retime, 'pointsNormalized')) {
    delete strip.retime.pointsNormalized
  }
  return normalized
}

export function migrateStripRetimePointsToNormalized(strip, clipLength = null) {
  return setStripRetimePointsNormalized(strip, getRawStripRetimePoints(strip), clipLength)
}
